/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Injectable} from '@angular/core';
import {UserPreference} from '../../models/user-preference.model';
import {User} from '../../models/user.model';
import {VehicleScenario} from '../../models/vehicle-scenario-dto.model';

/**
 * Created by michael faith on 3/12/2019.
 */

@Injectable({
  providedIn: 'root'
})
export class StateService {

  private readonly SYSTEM_ADMIN = 'System Admin';
  private readonly CENTRAL_OFFICE_ADMIN = 'Central Office Admin';
  private readonly REGIONAL_ADMIN = 'Regional Admin';
  private readonly USER_ROLE = 'User';
  private readonly INACTIVE_USER_ROLE = 'Inactive User';
  public user: User = null;
  public userPreferences: UserPreference[];
  selectedScenario = null;
  selectedCompareScenario = null;
  isVehicleScenarioSelected = null;

  /**
   * Test to see if the current user has any of the provided authorities
   * @param requiredAuthorities Array of authority names
   */
  public hasAnyAuthorities(requiredAuthorities: string[]): boolean {
    for (const authority of requiredAuthorities) {
      if (authority !== null &&
        this.user.authorities.indexOf(authority) > -1) {
        return true;
      }
    }
    return false;
  }

  /**
   * Test if the current user has the provided authority.
   * @param requiredAuthority Authority name
   */
  public hasAuthority(requiredAuthority: string): boolean {
    return this.hasAnyAuthorities([requiredAuthority]);
  }

  /**
   * Check to see if the current user is an admin
   */
  public isAdmin(): boolean {
    return this.user.role.includes('Admin');
  }

  /**
   * Check to see if the current user is a system admin.
   */
  public isSystemAdminRole(): boolean {
    return this.user.role === this.SYSTEM_ADMIN;
  }

  /**
   * Get the current user's GMID
   */
  public getUserGmid(): string {
    return this.user.gmid;
  }

  /**
   * Get the current user
   */
  public getUser(): User {
    return this.user;
  }

  /**
   * Check to see if the current user is a central office admin.
   */
  public isCentralOfficeAdminRole(): boolean {
    return this.user.role === this.CENTRAL_OFFICE_ADMIN;
  }

  /**
   * Check to see if the current user is a regional admin.
   */
  public isRegionalAdminRole(): boolean {
    return this.user.role === this.REGIONAL_ADMIN;
  }

  /**
   * Check to see if the current user is a user role.
   */
  public isUserRole(): boolean {
    return this.user.role === this.USER_ROLE;
  }

  /**
   * Check to see if the current user is an inactive user role.
   */
  public isInactiveUserRole(): boolean {
    return this.user.role === this.INACTIVE_USER_ROLE;
  }

  /**
   * Sets the persistent selected scenario
   * @param scenario The selected scenario
   * @param isVehicle Is true if the selected scenario is a vehicle scenario, false if it is an industry scenario
   */
  setSelectedScenario(scenario: object, isVehicle: boolean): void {
    this.selectedScenario = scenario;
    this.isVehicleScenarioSelected = isVehicle;
  }

  /**
   * Sets the persistent compare scenario
   * @param scenario The selected compare scenario
   */
  setCompareScenario(scenario: VehicleScenario): void {
    this.selectedCompareScenario = scenario;
  }

  /**
   * Sets the user preferences
   * @param preferences Array of UserPreference objects
   */
  setUserPreferences(preferences: UserPreference[]): void {
    this.userPreferences = preferences;
  }
}
